import type { Member, MemberGroupEnrollment } from "../../../../services/types";
import { DataTable, type DataTableColumn } from "../../../../components/feature/DataTable";
import { Badge } from "../../../../components/base/Badge";
import { CurrencyDisplay } from "../../../../components/currency/CurrencyDisplay";
import { formatDate } from "../../../../utils/format";

const ENROLL_STATUS_BADGE: Record<MemberGroupEnrollment["status"], "active" | "completed" | "blacklisted"> = {
  active: "active",
  completed: "completed",
  blacklisted: "cancelled",
};

export function GroupsTab({ member }: { member: Member }) {
  const columns: DataTableColumn<MemberGroupEnrollment>[] = [
    {
      id: "groupName",
      header: "Group",
      accessor: (e) => <span className="font-medium text-primary-700">{e.groupName}</span>,
      sortValue: (e) => e.groupName,
      sortable: true,
    },
    {
      id: "ticket",
      header: "Ticket No.",
      accessor: (e) => <span className="tnum text-xs">{e.ticketNumber}</span>,
      sortValue: (e) => e.ticketNumber,
      sortable: true,
    },
    {
      id: "installment",
      header: "Monthly Installment",
      align: "right",
      accessor: (e) => <CurrencyDisplay amount={e.monthlyInstallment} />,
      sortValue: (e) => e.monthlyInstallment,
      sortable: true,
      footerValue: () => <span className="text-xs font-medium text-foreground-500">—</span>,
    },
    {
      id: "joined",
      header: "Joined On",
      accessor: (e) => <span className="tnum">{formatDate(e.joinedOn)}</span>,
      sortValue: (e) => e.joinedOn,
      sortable: true,
    },
    {
      id: "won",
      header: "Won",
      accessor: (e) =>
        e.wonCycle ? (
          <Badge color="success">Cycle {e.wonCycle}</Badge>
        ) : (
          <span className="text-foreground-400">—</span>
        ),
      sortValue: (e) => e.wonCycle ?? 0,
      sortable: true,
    },
    {
      id: "outstanding",
      header: "Outstanding",
      align: "right",
      accessor: (e) =>
        e.outstanding > 0 ? (
          <CurrencyDisplay amount={e.outstanding} tone="danger" />
        ) : (
          <CurrencyDisplay amount={e.outstanding} className="text-foreground-400" />
        ),
      sortValue: (e) => e.outstanding,
      sortable: true,
      footerValue: (rows) => (
        <CurrencyDisplay amount={rows.reduce((s, e) => s + e.outstanding, 0)} />
      ),
    },
    {
      id: "status",
      header: "Status",
      accessor: (e) => <Badge color={ENROLL_STATUS_BADGE[e.status]} dot>{e.status}</Badge>,
      sortValue: (e) => e.status,
      sortable: true,
    },
  ];

  return (
    <DataTable<MemberGroupEnrollment>
      columns={columns}
      rows={member.enrollments}
      rowKey={(e) => `${e.groupId}`}
      emptyMessage="Not enrolled in any group yet"
      emptyDescription="Group participation appears here after enrolment (Phase 2)."
      showTotals
      totalLabel="Totals"
      defaultPageSize={10}
    />
  );
}